import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { AdminTrainingModuleRow, AdminTrainingTrack } from "@/api/admin-api"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"

import { RawJsonTab } from "./shared/raw-json-tab"

type GatingGroup = {
  key: string
  title: string
  modules: AdminTrainingModuleRow[]
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoadingGating: boolean
  isSaving: boolean
  modules: AdminTrainingModuleRow[]
  tracks: AdminTrainingTrack[]
  selectedModuleIds: string[]
  onToggleModule: (moduleId: string) => void
  onToggleTrack: (trackModuleIds: string[]) => void
  onSave: () => void
  /** Raw JSON value of the gating config (for the Raw tab). */
  rawValue?: unknown
  /** Called when the Raw tab is saved (with parsed value + reason). */
  onRawSave?: (next: unknown, reason: string) => Promise<void>
}

function computeGroupState(
  groupModuleIds: string[],
  selected: string[]
): boolean | "indeterminate" {
  const selectedCount = groupModuleIds.filter((id) => selected.includes(id)).length
  if (selectedCount === 0) return false
  if (selectedCount === groupModuleIds.length) return true
  return "indeterminate"
}

function useGatingGroups(
  modules: AdminTrainingModuleRow[],
  tracks: AdminTrainingTrack[],
  otherLabel: string
): GatingGroup[] {
  return useMemo(() => {
    const moduleById = new Map(modules.map((m) => [m.id, m]))
    const groups: GatingGroup[] = []
    const assignedIds = new Set<string>()

    const sortedTracks = [...tracks].sort((a, b) => a.order - b.order)
    for (const track of sortedTracks) {
      const trackModules = track.module_ids
        .map((id) => moduleById.get(id))
        .filter((m): m is AdminTrainingModuleRow => Boolean(m))
      if (trackModules.length === 0) continue
      trackModules.forEach((m) => assignedIds.add(m.id))
      groups.push({ key: track.id, title: track.display_name, modules: trackModules })
    }

    const ungrouped = modules.filter((m) => !assignedIds.has(m.id))
    if (ungrouped.length > 0) {
      groups.push({ key: "__other__", title: otherLabel, modules: ungrouped })
    }

    return groups
  }, [modules, tracks, otherLabel])
}

function GatingGroupSection({
  group,
  selectedModuleIds,
  onToggleModule,
  onToggleTrack,
}: {
  group: GatingGroup
  selectedModuleIds: string[]
  onToggleModule: (moduleId: string) => void
  onToggleTrack: (trackModuleIds: string[]) => void
}) {
  const { t } = useTranslation()
  const groupModuleIds = group.modules.map((m) => m.id)
  const selectedInGroup = groupModuleIds.filter((id) => selectedModuleIds.includes(id)).length
  const groupState = computeGroupState(groupModuleIds, selectedModuleIds)

  return (
    <section key={group.key} className="space-y-2">
      <div className="flex items-center justify-between border-b pb-2">
        <label className="flex cursor-pointer items-center gap-3">
          <Checkbox
            checked={groupState}
            aria-label={t("adminDashboard.trainingManagement.gatingToggleTrack", {
              defaultValue: "Toggle all modules in {{track}}",
              track: group.title,
            })}
            onCheckedChange={() => onToggleTrack(groupModuleIds)}
          />
          <span className="text-sm font-semibold">{group.title}</span>
        </label>
        <span className="text-muted-foreground text-xs">
          {t("adminDashboard.trainingManagement.gatingSelectedCount", {
            defaultValue: "{{selected}} / {{total}} selected",
            selected: selectedInGroup,
            total: groupModuleIds.length,
          })}
        </span>
      </div>
      <div className="space-y-2 pl-6">
        {group.modules.map((mod) => (
          <label
            key={mod.id}
            className="hover:bg-muted/50 flex cursor-pointer items-center gap-3 rounded-md border p-3"
          >
            <Checkbox
              checked={selectedModuleIds.includes(mod.id)}
              onCheckedChange={() => onToggleModule(mod.id)}
            />
            <span className="text-sm font-medium">{mod.display_name}</span>
          </label>
        ))}
      </div>
    </section>
  )
}

function GatingDialogBody({
  isLoadingGating,
  groups,
  selectedModuleIds,
  onToggleModule,
  onToggleTrack,
}: {
  isLoadingGating: boolean
  groups: GatingGroup[]
  selectedModuleIds: string[]
  onToggleModule: (moduleId: string) => void
  onToggleTrack: (trackModuleIds: string[]) => void
}) {
  const { t } = useTranslation()

  if (isLoadingGating) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    )
  }

  if (groups.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("adminDashboard.trainingManagement.gatingNoModules", {
          defaultValue: "No training modules available.",
        })}
      </p>
    )
  }

  return (
    <>
      {groups.map((group) => (
        <GatingGroupSection
          key={group.key}
          group={group}
          selectedModuleIds={selectedModuleIds}
          onToggleModule={onToggleModule}
          onToggleTrack={onToggleTrack}
        />
      ))}
    </>
  )
}

export function TrainingGatingRequirementsDialog({
  open,
  onOpenChange,
  isLoadingGating,
  isSaving,
  modules,
  tracks,
  selectedModuleIds,
  onToggleModule,
  onToggleTrack,
  onSave,
  rawValue,
  onRawSave,
}: Props) {
  const { t } = useTranslation()
  const otherLabel = t("adminDashboard.trainingManagement.gatingOtherGroup", {
    defaultValue: "Other",
  })
  const groups = useGatingGroups(modules, tracks, otherLabel)
  const showRawTab = rawValue !== undefined && onRawSave !== undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.trainingManagement.gatingTitle", {
              defaultValue: "Gating requirements",
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.trainingManagement.gatingDescription", {
              defaultValue:
                "Select which training modules must be completed before users can access the application.",
            })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.trainingManagement.gatingTabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            {showRawTab ? (
              <TabsTrigger
                className="text-destructive data-[state=active]:text-destructive"
                value="raw"
              >
                {t("adminDashboard.trainingManagement.gatingTabRaw", { defaultValue: "Raw JSON" })}
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="form">
            <div className="max-h-[60vh] space-y-5 overflow-y-auto py-4 pr-1">
              <GatingDialogBody
                groups={groups}
                isLoadingGating={isLoadingGating}
                selectedModuleIds={selectedModuleIds}
                onToggleModule={onToggleModule}
                onToggleTrack={onToggleTrack}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t("common.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button disabled={isSaving} onClick={onSave}>
                {isSaving
                  ? t("common.saving", { defaultValue: "Saving..." })
                  : t("common.save", { defaultValue: "Save" })}
              </Button>
            </DialogFooter>
          </TabsContent>

          {showRawTab ? (
            <TabsContent className="mt-4" value="raw">
              <RawJsonTab
                confirmToken="training-gating"
                isPending={isSaving}
                value={rawValue}
                resourceLabel={t("adminDashboard.trainingManagement.gatingRawResourceLabel", {
                  defaultValue: "training gating",
                })}
                onSave={onRawSave}
              />
            </TabsContent>
          ) : null}
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
